import { createSelector } from 'reselect';
import getParent from '../selectors';
import { flatten } from 'lodash';
import {
  getFrequencyDataSet,
  getFilteredParameterValues,
  isFrequencyOrContingencyTable,
  getFilteredParametersValuesNotEmpty,
  getVisibleNumericalSelectedParametersNames,
  getVisibleCategoricalSelectedParametersNames
} from 'store/dataset';
import {
  setParametersInferentialStatistics,
  resetParametersInferentialStatistics
} from 'store/scenes/inferentialStatistics';
import { resetInferentialStatistics } from 'store/form/inferentialStatistics';
import {
  isDatasetContainsRawData
} from 'store/form/inferentialStatistics/pairedT/selectors';
import { isAnovaModeDatasetContainsRawData } from 'store/form/inferentialStatistics/anova';

import {
  calculateTableDataForOneSampleTHypothesisMean,
  calculateTableDataForOneSampleTConfidenceInterval
} from 'calculators/inferentialStatistics/oneSampleT';
import {
  makeProportionConfidenceLimits,
  makeProportionHypothesizedProportion,
  calculateTableDataForOneSampleProportion
} from 'calculators/inferentialStatistics/oneSampleProportion';
import {
  makeTwoSampleTHypothesis,
  makeTwoSampleTConfidenceLimits,
  calculateTableDataForTwoSampleT
} from 'calculators/inferentialStatistics/twoSampleT';
import {
  makeTwoProportionHypothesizedProportion,
  makeTwoProportionConfidenceLimits,
  calculateTableDataForTwoSampleProportion
} from 'calculators/inferentialStatistics/twoSampleProportion';
import {
  makePairedTHypothesizedProportion,
  makePairedTHypothesizedProportionForTwoGroup,
  makePairedTConfidenceLimits,
  makePairedTConfidenceLimitsForTwoGroup,
  calculateTableDataForPairedT,
  calculateTableDataForPairedTForTwoGroups
} from 'calculators/inferentialStatistics/pairedT';
import { calculateTableDataForChiSquareGoodnessOfFit, calculateChiSquareGoodnessOfFitSummary } from 'calculators/inferentialStatistics/chiSquareGoodnessOfFit';
import { calculateChiSquareTestForIndependence, calculateTableDataForChiSquareTestForIndependence } from 'calculators/inferentialStatistics/chiSquareTestForIndependence';
import {
  calculateTableStatisticsAnova,
  calculateTableForAnalysisAnova,
  recalculateTableForTukeyKramerPostHocAnalysis,
  calculateDataForAnovaWithDataSetScenario,
  calculateTukeyKramerAnalysisNoDataset
} from 'calculators/inferentialStatistics/anova';

import {
  ONE_SAMPLE_T,
  HYPOTHESIS_MEAN,
  CONFIDENCE_INTERVAL,
  ONE_SAMPLE_PROPORTION,
  TWO_SAMPLE_T,
  TWO_SAMPLE_PROPORTION,
  PAIRED_T,
  CHI_SQUARE_GOODNESS_OF_FIT,
  CHI_SQUARE_TEST_FOR_HOMOGENEITY,
  CHI_SQUARE_TEST_FOR_INDEPENDENCE,
  ANOVA
} from 'scenes/InferentialStatistics/Constants';
import { round6 } from 'utilities/math';

export default state => getParent(state).inferentialStatistics;

export const isInferentialStatisticsCalculated = state => {
  return getParent(state).inferentialStatistics.isCalculated;
};

export const getAltHypotesisSign = state => {
  return getParent(state).inferentialStatistics.altHypothesisSign;
};

export const getH0 = state => {
  return getParent(state).inferentialStatistics.h0;
};

export const getH1 = state => {
  return getParent(state).inferentialStatistics.h1;
};

export const getTableData = state => {
  return getParent(state).inferentialStatistics.tableData;
};

export const getTableStat = state => {
  return getParent(state).inferentialStatistics.tableDataStat;
};

export const getProcedure = state => {
  return getParent(state).inferentialStatistics.procedure;
};

export const getConfidenceInterval = state => {
  return getParent(state).inferentialStatistics.confidenceInterval;
};

export const getNumericalRowDataSet = state => {
  return getParent(state).inferentialStatistics.numericalRowDataSet;
};

export const getCategoricalRowDataSet = state => {
  return getParent(state).inferentialStatistics.categoricalRowDataSet;
};

export const getLevelsRowDataSet = state => {
  return getParent(state).inferentialStatistics.levelsRowDataSet;
};

export const getInferentialStatistics = state => {
  return getParent(state).inferentialStatistics.inferentialStatistics;
};

export const getLowerLimit = state => {
  return getParent(state).inferentialStatistics.lowerLimit;
};

export const getUpperLimit = state => {
  return getParent(state).inferentialStatistics.upperLimit;
};

export const getLimits = createSelector(
  getLowerLimit,
  getUpperLimit,
  (lowerLimit, upperLimit) => ({ lowerLimit, upperLimit })
);

export const getPValue = state => {
  return getParent(state).inferentialStatistics.pValue;
};

export const getTStatistics = state => {
  return getParent(state).inferentialStatistics.tStatistics;
};

export const getStandardError = state => {
  return getParent(state).inferentialStatistics.standardError;
};

export const getDfValue = state => {
  return getParent(state).inferentialStatistics.dfValue;
};

export const getDF = state => {
  return getParent(state).inferentialStatistics.df;
};

export const getIsGroupChosen = state => {
  return getParent(state).inferentialStatistics.isGroupChosen;
};

export const getPopulationProportionCommon = state => {
  return getParent(state).inferentialStatistics.populationProportionCommon;
};

export const getChiSquare = state => {
  return getParent(state).inferentialStatistics.chiSquare;
};

export const getN = state => {
  return getParent(state).inferentialStatistics.n;
};

export const getMeanOfSampleDiff = state => {
  return getParent(state).inferentialStatistics.meanOfSampleDiff;
};

export const getPairedTTwoGroups = state => {
  return getParent(state).inferentialStatistics.hasPairedTTwoGroups;
};

export const getHasNumericalVariablesEmptyCell = state =>
  getParent(state).inferentialStatistics.hasNumericalVariablesEmptyCell;

export const getHasCategoricalVariablesEmptyCell = state =>
  getParent(state).inferentialStatistics.hasCategoricalVariablesEmptyCell;

export const isAnovaDataSetScenario = state =>
  getParent(state).inferentialStatistics.isAnovaDataSetScenario;

export const getActiveIndexTabAnova = state =>
  getParent(state).inferentialStatistics.activeIndexTabAnova;

export const getAnovaConfidenceInterval = state =>
  getParent(state).inferentialStatistics.anovaConfidenceInterval;

export const getTableForTukeyKramerPostHocAnalysis = state =>
  getParent(state).inferentialStatistics.tableForTukeyKramerPostHocAnalysis;

export const getQForStudentizedRangeDistribution = state =>
  getParent(state).inferentialStatistics.qForStudentizedRangeDistribution;

export const generateSamples = () => (dispatch, getState) => {
  const state = getState();

  const {
    hypothesisMean,
    oneSampleT: {
      sampleMean,
      sampleSize,
      standardDeviation
    },
    twoSampleT,
    alternativeHypothesis,
    dfHypothesisProportion,
    confidenceInterval,
    procedureOneSample,
    inferentialStatistics,
    oneSampleProportion: {
      numberOfSuccess,
      numberOfTrials,
      categoricalRowDataSet,
      levelsRowDataSet
    },
    chiSquare: {
      observedCounts,
      ...categoriesInfo
    },
    twoSampleProportion,
    hypothesisProportion,
    pairedT,
    anova: {
      groups,
      anovaConfidenceInterval,
      numericalRowDataSetAnova,
      categoricalRowDataSetAnova
    }
  } = state.form.INFERENTIAL_STATISTICS.values;
  const frequencyDataSet = getFrequencyDataSet(state);

  let valuesToSentToScene = null;

  if (procedureOneSample === HYPOTHESIS_MEAN && inferentialStatistics === ONE_SAMPLE_T) {
    valuesToSentToScene = {
      altHypothesisSign: alternativeHypothesis,
      h0: hypothesisMean,
      h1: hypothesisMean,
      tableData: calculateTableDataForOneSampleTHypothesisMean(
        Number(hypothesisMean), Number(sampleMean), Number(sampleSize), Number(standardDeviation), alternativeHypothesis),
      isCalculated: true,
      procedure: procedureOneSample,
      inferentialStatistics
    };
  } else if (procedureOneSample === CONFIDENCE_INTERVAL && inferentialStatistics === ONE_SAMPLE_T) {
    valuesToSentToScene = {
      tableData: calculateTableDataForOneSampleTConfidenceInterval(sampleMean, sampleSize, standardDeviation, (confidenceInterval / 100)),
      isCalculated: true,
      procedure: procedureOneSample,
      inferentialStatistics,
      // e.g. 90% to 0.9
      confidenceInterval: Number(confidenceInterval)
    };
  } else if (procedureOneSample === HYPOTHESIS_MEAN && inferentialStatistics === ONE_SAMPLE_PROPORTION) {
    const { standardError, ...otherData } = makeProportionHypothesizedProportion(Number(numberOfSuccess), Number(numberOfTrials), Number(hypothesisProportion), alternativeHypothesis);
    valuesToSentToScene = {
      altHypothesisSign: alternativeHypothesis,
      tableData: calculateTableDataForOneSampleProportion(numberOfSuccess, numberOfTrials, categoricalRowDataSet, levelsRowDataSet, standardError),
      procedure: procedureOneSample,
      inferentialStatistics,
      categoricalRowDataSet,
      levelsRowDataSet,
      ...otherData,
      isCalculated: true
    };
  } else if (procedureOneSample === CONFIDENCE_INTERVAL && inferentialStatistics === ONE_SAMPLE_PROPORTION) {
    const { standardError, ...otherData } = makeProportionConfidenceLimits(Number(numberOfSuccess), Number(numberOfTrials), (confidenceInterval / 100));
    valuesToSentToScene = {
      tableData: calculateTableDataForOneSampleProportion(numberOfSuccess, numberOfTrials, categoricalRowDataSet, levelsRowDataSet, standardError),
      procedure: procedureOneSample,
      inferentialStatistics,
      categoricalRowDataSet,
      levelsRowDataSet,
      ...otherData,
      confidenceInterval: Number(confidenceInterval),
      isCalculated: true
    };
  } else if (procedureOneSample === HYPOTHESIS_MEAN && inferentialStatistics === TWO_SAMPLE_T) {
    const { standardErrors, ...otherData } = makeTwoSampleTHypothesis({ ...twoSampleT, alternativeHypothesis, hypothesisMean });
    valuesToSentToScene = {
      altHypothesisSign: alternativeHypothesis,
      tableData: calculateTableDataForTwoSampleT({ ...twoSampleT, ...standardErrors }),
      procedure: procedureOneSample,
      inferentialStatistics,
      ...otherData,
      isCalculated: true
    };
  } else if (procedureOneSample === CONFIDENCE_INTERVAL && inferentialStatistics === TWO_SAMPLE_T) {
    const { standardErrors, ...otherData } = makeTwoSampleTConfidenceLimits({ ...twoSampleT, confidenceInterval });
    valuesToSentToScene = {
      tableData: calculateTableDataForTwoSampleT({ ...twoSampleT, ...standardErrors }),
      procedure: procedureOneSample,
      inferentialStatistics,
      ...otherData,
      confidenceInterval: Number(confidenceInterval),
      isCalculated: true
    };
  } else if (procedureOneSample === HYPOTHESIS_MEAN && inferentialStatistics === TWO_SAMPLE_PROPORTION) {
    const hypothesizedProportionData = makeTwoProportionHypothesizedProportion({ ...twoSampleProportion, alternativeHypothesis, dfHypothesisProportion });
    const isGroupChosen = twoSampleProportion.successGroupFirst || twoSampleProportion.successGroupSecond;
    valuesToSentToScene = {
      altHypothesisSign: alternativeHypothesis,
      tableData: calculateTableDataForTwoSampleProportion(twoSampleProportion, isGroupChosen),
      procedure: procedureOneSample,
      isGroupChosen,
      inferentialStatistics,
      ...hypothesizedProportionData,
      isCalculated: true
    };
  } else if (procedureOneSample === CONFIDENCE_INTERVAL && inferentialStatistics === TWO_SAMPLE_PROPORTION) {
    const confidenceIntervalProportionData = makeTwoProportionConfidenceLimits({ ...twoSampleProportion, confidenceInterval });
    const isGroupChosen = twoSampleProportion.successGroupFirst || twoSampleProportion.successGroupSecond;
    valuesToSentToScene = {
      tableData: calculateTableDataForTwoSampleProportion(twoSampleProportion, isGroupChosen),
      procedure: procedureOneSample,
      isGroupChosen,
      inferentialStatistics,
      ...confidenceIntervalProportionData,
      confidenceInterval: Number(confidenceInterval),
      isCalculated: true
    };
  } else if (procedureOneSample === HYPOTHESIS_MEAN && inferentialStatistics === PAIRED_T) {
    const lengthDataSet = getVisibleNumericalSelectedParametersNames(state).length;
    const isRawData = isDatasetContainsRawData(state);
    const hasPairedTTwoGroups = lengthDataSet > 1 && isRawData;
    let hypothesisPairedTData = {};
    let tableData = [];
    if (hasPairedTTwoGroups) {
      const hasDataEmptyCells = getHasNumericalVariablesEmptyCell(state);
      let firstVariable = [];
      let secondVariable = [];
      if (hasDataEmptyCells) {
        const variables = getFilteredParametersValuesNotEmpty(state, pairedT.numericalRowDataSetFirst, pairedT.numericalRowDataSetSecond);
        firstVariable = variables.firstParsedParameterValues;
        secondVariable = variables.secondParsedParameterValues;
      } else {
        firstVariable = getFilteredParameterValues(state, pairedT.numericalRowDataSetFirst);
        secondVariable = getFilteredParameterValues(state, pairedT.numericalRowDataSetSecond);
      }
      hypothesisPairedTData = makePairedTHypothesizedProportionForTwoGroup({ ...pairedT, alternativeHypothesis, firstVariable, secondVariable });
      tableData = calculateTableDataForPairedTForTwoGroups(pairedT);
    } else {
      hypothesisPairedTData = makePairedTHypothesizedProportion({ ...pairedT, alternativeHypothesis });
      tableData = calculateTableDataForPairedT(pairedT, lengthDataSet === 1);
    }
    valuesToSentToScene = {
      altHypothesisSign: alternativeHypothesis,
      tableData,
      hasPairedTTwoGroups,
      procedure: procedureOneSample,
      numericalRowDataSet: pairedT.numericalRowDataSet,
      inferentialStatistics,
      ...hypothesisPairedTData,
      isCalculated: true
    };
  } else if (procedureOneSample === CONFIDENCE_INTERVAL && inferentialStatistics === PAIRED_T) {
    const lengthDataSet = getVisibleNumericalSelectedParametersNames(state).length;
    const isRawData = isDatasetContainsRawData(state);
    const hasPairedTTwoGroups = lengthDataSet > 1 && isRawData;
    let confidenceIntervalPairedTData = {};

    let tableData = [];
    if (hasPairedTTwoGroups) {
      const hasDataEmptyCells = getHasNumericalVariablesEmptyCell(state);
      let firstVariable = [];
      let secondVariable = [];
      if (hasDataEmptyCells) {
        const variables = getFilteredParametersValuesNotEmpty(state, pairedT.numericalRowDataSetFirst, pairedT.numericalRowDataSetSecond);
        firstVariable = variables.firstParsedParameterValues;
        secondVariable = variables.secondParsedParameterValues;
      } else {
        firstVariable = getFilteredParameterValues(state, pairedT.numericalRowDataSetFirst);
        secondVariable = getFilteredParameterValues(state, pairedT.numericalRowDataSetSecond);
      }
      confidenceIntervalPairedTData = makePairedTConfidenceLimitsForTwoGroup({ firstVariable, secondVariable, confidenceInterval });
      tableData = calculateTableDataForPairedTForTwoGroups(pairedT);
    } else {
      confidenceIntervalPairedTData = makePairedTConfidenceLimits({ ...pairedT, confidenceInterval });
      tableData = calculateTableDataForPairedT(pairedT, lengthDataSet === 1);
    }
    valuesToSentToScene = {
      tableData,
      hasPairedTTwoGroups,
      inferentialStatistics,
      procedure: procedureOneSample,
      numericalRowDataSet: pairedT.numericalRowDataSet,
      ...confidenceIntervalPairedTData,
      confidenceInterval: Number(confidenceInterval),
      isCalculated: true
    };
  } else if (inferentialStatistics === CHI_SQUARE_GOODNESS_OF_FIT) {
    const chiSquareTableData = calculateTableDataForChiSquareGoodnessOfFit(frequencyDataSet, observedCounts, [], categoriesInfo[observedCounts]);
    const chiSquareSummary = calculateChiSquareGoodnessOfFitSummary(frequencyDataSet, observedCounts, chiSquareTableData.contributionChiSquare);

    const tableData = chiSquareTableData.levelNames.map(
      (levelName, i) =>
        [levelName, chiSquareTableData.observedCount[i], chiSquareTableData.expected[i], round6(chiSquareTableData.contributionChiSquare[i])]
    );

    valuesToSentToScene = {
      isCalculated: true,
      tableData,
      categoricalRowDataSet: observedCounts,
      inferentialStatistics,
      ...chiSquareSummary
    };
  } else if (inferentialStatistics === CHI_SQUARE_TEST_FOR_INDEPENDENCE || inferentialStatistics === CHI_SQUARE_TEST_FOR_HOMOGENEITY) {
    const chiSquareTableData = calculateTableDataForChiSquareTestForIndependence(frequencyDataSet, observedCounts);
    const chiSquareSummary = calculateChiSquareTestForIndependence(frequencyDataSet, observedCounts, flatten(chiSquareTableData.contributionsToChiSquare));
    valuesToSentToScene = {
      isCalculated: true,
      tableData: chiSquareTableData,
      levelsRowDataSet: chiSquareTableData.levelColumnNames,
      categoricalRowDataSet: observedCounts,
      inferentialStatistics,
      ...chiSquareSummary
    };
  } else if (inferentialStatistics === ANOVA) {
    const isRawData = isAnovaModeDatasetContainsRawData(state);
    const lengthNumericalDataSet = isFrequencyOrContingencyTable(state) ? 0 : getVisibleNumericalSelectedParametersNames(state).length;
    const lengthCategoricalDataSet = getVisibleCategoricalSelectedParametersNames(state).length;
    const isDataSetScenario = !isFrequencyOrContingencyTable(state) && lengthNumericalDataSet > 0 && lengthCategoricalDataSet > 0 && isRawData;
    const hasDataEmptyCells = getHasNumericalVariablesEmptyCell(state) || getHasCategoricalVariablesEmptyCell(state);
    let numericalVariable = [];
    let categoricalVariable = [];
    if (hasDataEmptyCells) {
      const variables = getFilteredParametersValuesNotEmpty(state, numericalRowDataSetAnova, categoricalRowDataSetAnova);
      numericalVariable = variables.firstParsedParameterValues;
      categoricalVariable = variables.secondParsedParameterValues;
    } else {
      numericalVariable = getFilteredParameterValues(state, numericalRowDataSetAnova);
      categoricalVariable = getFilteredParameterValues(state, categoricalRowDataSetAnova);
    }

    if (isDataSetScenario) {
      valuesToSentToScene = {
        isCalculated: true,
        inferentialStatistics,
        anovaConfidenceInterval,
        isAnovaDataSetScenario: isDataSetScenario,
        numericalRowDataSet: numericalRowDataSetAnova,
        categoricalRowDataSet: categoricalRowDataSetAnova,
        ...calculateDataForAnovaWithDataSetScenario(categoricalVariable, numericalVariable, anovaConfidenceInterval)
      };
    } else {
      const { tableForAnalysis, meanSquareForError } = calculateTableForAnalysisAnova(groups);
      valuesToSentToScene = {
        anovaConfidenceInterval,
        isAnovaDataSetScenario: isDataSetScenario,
        tableData: tableForAnalysis,
        tableDataStat: calculateTableStatisticsAnova(groups, anovaConfidenceInterval, meanSquareForError),
        isCalculated: true,
        inferentialStatistics,
        ...calculateTukeyKramerAnalysisNoDataset(groups, anovaConfidenceInterval)
      };
    }
  }
  dispatch(setParametersInferentialStatistics(valuesToSentToScene));
};

export const setAnovaConfidenceInterval = () => (dispatch, getState) => {
  const state = getState();
  const {
    anovaConfidenceInterval,
    numericalRowDataSetAnova,
    categoricalRowDataSetAnova,
    groups
  } = state.form.INFERENTIAL_STATISTICS.values.anova;

  const isRawData = isAnovaModeDatasetContainsRawData(state);
  const isDatasetScenario = numericalRowDataSetAnova && categoricalRowDataSetAnova && isRawData;
  // DatasetScenario
  if (isDatasetScenario) {
    const hasDataEmptyCells = getHasNumericalVariablesEmptyCell(state) || getHasCategoricalVariablesEmptyCell(state);
    let numericalVariable = [];
    let categoricalVariable = [];
    if (hasDataEmptyCells) {
      const variables = getFilteredParametersValuesNotEmpty(state, numericalRowDataSetAnova, categoricalRowDataSetAnova);
      numericalVariable = variables.firstParsedParameterValues;
      categoricalVariable = variables.secondParsedParameterValues;
    } else {
      numericalVariable = getFilteredParameterValues(state, numericalRowDataSetAnova);
      categoricalVariable = getFilteredParameterValues(state, categoricalRowDataSetAnova);
    }
    const valuesToSentToScene = {
      anovaConfidenceInterval,
      ...recalculateTableForTukeyKramerPostHocAnalysis(categoricalVariable, numericalVariable, anovaConfidenceInterval)
    };
    dispatch(setParametersInferentialStatistics(valuesToSentToScene));
  } else {
    const valuesToSentToScene = {
      anovaConfidenceInterval,
      ...calculateTukeyKramerAnalysisNoDataset(groups, anovaConfidenceInterval)
    };
    dispatch(setParametersInferentialStatistics(valuesToSentToScene));
  }
};

export const handleResetClick = () => dispatch => {
  dispatch(resetParametersInferentialStatistics());
  dispatch(resetInferentialStatistics());
};
